import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SydButton from '../../../commonDesign/Button'
import FlexColumn from '../../../molecules/FlexColumn'
import { ICON_NAMES } from '../../../../constants'

const USER_CALLBACK_MESSAGE_TYPE = 'denali:oauth-user-callback'
const POPUP_WIDTH = 560
const POPUP_HEIGHT = 680
// Cross-origin popups hide their lifecycle events; poll `closed` instead.
const POPUP_POLL_INTERVAL_MS = 500
// `.` is outside the URL-safe base64 alphabet, so this prefix can't collide
// with admin-flow states consumed by `CallbackUtility` (which base64-decodes
// state to an authorizationId).
const USER_STATE_PREFIX = 'u.'

// CSRF `state` must come from a CSPRNG — never degrade to `Math.random`.
function generateState () {
  if (typeof crypto === 'undefined') {
    throw new Error('Secure random number generation is not available in this browser')
  }
  let random
  if (typeof crypto.randomUUID === 'function') {
    random = crypto.randomUUID()
  } else {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    random = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  }
  return `${USER_STATE_PREFIX}${random}`
}

function buildAuthorizeUrl (authorizationUrl, clientId, redirectUri, scope, responseType, state) {
  const url = new URL(authorizationUrl)
  url.searchParams.set('client_id', clientId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('response_type', responseType || 'code')
  url.searchParams.set('state', state)
  if (scope) url.searchParams.set('scope', scope)
  return url.toString()
}

function OAuthConnectButton ({
  domain,
  authorizationUrl,
  clientId,
  redirectUri,
  scope,
  responseType,
  label,
  onAuthorized,
  onError,
  disabled
}) {
  const [processing, setProcessing] = useState(false)
  const popupRef = useRef(null)
  const pollTimerRef = useRef(null)
  const stateRef = useRef(null)
  const onAuthorizedRef = useRef(onAuthorized)
  const onErrorRef = useRef(onError)

  useEffect(() => { onAuthorizedRef.current = onAuthorized }, [onAuthorized])
  useEffect(() => { onErrorRef.current = onError }, [onError])

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const closePopup = useCallback(() => {
    stopPolling()
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close() } catch (_err) { /* ignore */ }
    }
    popupRef.current = null
  }, [stopPolling])

  // Reset `processing` if the user dismisses the popup before the callback
  // fires; cross-origin policy hides the popup's lifecycle events.
  const startPollingForClose = useCallback(() => {
    stopPolling()
    pollTimerRef.current = setInterval(() => {
      const popup = popupRef.current
      if (!popup || popup.closed) {
        stopPolling()
        popupRef.current = null
        if (stateRef.current) {
          stateRef.current = null
          setProcessing(false)
          onErrorRef.current?.(new Error('Authorization was cancelled before it completed.'))
        }
      }
    }, POPUP_POLL_INTERVAL_MS)
  }, [stopPolling])

  useEffect(() => {
    function handleMessage (event) {
      if (event.origin !== window.location.origin) return
      const data = event.data
      if (!data || data.type !== USER_CALLBACK_MESSAGE_TYPE) return
      if (data.state !== stateRef.current) return

      stateRef.current = null
      closePopup()
      setProcessing(false)

      if (data.error) {
        onErrorRef.current?.(new Error(data.errorDescription || data.error))
        return
      }
      if (!data.code) {
        onErrorRef.current?.(new Error('No authorization code returned by provider'))
        return
      }
      onAuthorizedRef.current?.({ code: data.code })
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [closePopup])

  useEffect(() => () => closePopup(), [closePopup])

  const handleClick = useCallback(() => {
    if (processing) return

    let state
    try {
      state = generateState()
    } catch (err) {
      onErrorRef.current?.(err)
      return
    }
    stateRef.current = state

    const callbackUrl = `${window.location.origin}/external/callback/${domain}`
    const effectiveRedirect = redirectUri || callbackUrl

    const authUrl = buildAuthorizeUrl(
      authorizationUrl,
      clientId,
      effectiveRedirect,
      scope,
      responseType,
      state
    )

    const top = window.screenY + Math.max(0, (window.innerHeight - POPUP_HEIGHT) / 2)
    const left = window.screenX + Math.max(0, (window.innerWidth - POPUP_WIDTH) / 2)
    const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top},left=${left},resizable,scrollbars`

    const popup = window.open(authUrl, `${domain}_oauth_connect`, features)
    if (!popup) {
      onErrorRef.current?.(new Error('Popup blocked. Please allow popups to connect.'))
      stateRef.current = null
      return
    }
    popupRef.current = popup
    setProcessing(true)
    startPollingForClose()
  }, [authorizationUrl, clientId, domain, processing, redirectUri, responseType, scope, startPollingForClose])

  return (
    <FlexColumn>
      <SydButton
        variant='primary'
        size='md'
        icon={ICON_NAMES.link}
        onClick={handleClick}
        disabled={disabled}
        processing={processing}
      >
        {label}
      </SydButton>
    </FlexColumn>
  )
}

OAuthConnectButton.propTypes = {
  domain: PropTypes.string.isRequired,
  authorizationUrl: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  redirectUri: PropTypes.string,
  scope: PropTypes.string,
  responseType: PropTypes.string,
  label: PropTypes.string,
  onAuthorized: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool
}

OAuthConnectButton.defaultProps = {
  label: 'Authorize',
  responseType: 'code'
}

export default OAuthConnectButton
